import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Link, useLocation } from 'wouter';
import { martinaRoomPath } from '@/lib/martinaRoomNavigation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMartinaOperatorNotificationCount } from '@/hooks/useMartinaOperatorNotificationCount';

type OperationalRoom = {
  id: number;
  title: string;
  identityTitle?: string | null;
  score?: number;
  attentionLane?: 'Alta' | 'Media' | 'Bassa';
  brief?: string | null;
  presentation?: { minimumMove?: string | null } | null;
};

type OperationalRoomPayload = { rooms?: OperationalRoom[] };

function firstLine(value: string | null | undefined): string | null {
  return value?.split('\n').map((line) => line.trim()).find(Boolean) ?? null;
}

function NotificationIndicator() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  
  // La campanella e il launcher leggono la stessa coda di Stanze, non lo
  // storico delle notifiche. Il contenuto del pannello deve quindi essere la
  // stessa graduatoria, altrimenti il numero nel badge non sarebbe verificabile.
  const { data: operationalCount = 0 } = useMartinaOperatorNotificationCount();
  
  const {
    data: focusData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['/api/exai/rooms/focus', { surface: 'notification-indicator', limit: 10 }],
    queryFn: () => apiClient('/api/exai/rooms/focus?limit=10&attentionBoard=1', { method: 'GET' }) as Promise<OperationalRoomPayload>,
    enabled: open,
    refetchInterval: open ? 10 * 1000 : false,
    staleTime: 5 * 1000,
  });
  const rooms = Array.isArray(focusData?.rooms) ? focusData.rooms : [];
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-11 w-11"
          aria-label={operationalCount > 0 ? `Apri ${operationalCount} priorità operative` : 'Apri le priorità operative'}
        >
          <Bell className="h-5 w-5" />
          {operationalCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-none bg-destructive text-xs font-medium text-destructive-foreground">
              {operationalCount > 9 ? '9+' : operationalCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="max-h-[calc(100dvh-5rem)] w-[min(380px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border-border bg-background shadow-lg"
      >
        <div className="flex items-center justify-between bg-muted px-4 py-3">
          <div>
            <h3 className="text-sm font-medium text-foreground">Priorità operative</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              La stessa coda di Martina visibile nella Board.
            </p>
          </div>
          <span className="text-sm font-semibold text-foreground">{operationalCount}</span>
        </div>
        <div className="h-px bg-border"></div>
        
        {isLoading ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 h-4 w-4 animate-spin rounded-none border-2 border-primary border-t-transparent"></div>
            <p className="text-sm text-muted-foreground">Martina sta ordinando le priorità...</p>
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <p className="text-sm font-medium text-foreground">Le priorità non sono disponibili in questo momento</p>
            <p className="mt-1 text-xs text-muted-foreground">Martina ha registrato il problema; puoi riprovare dalla dashboard.</p>
          </div>
        ) : rooms.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-none bg-muted">
              <Bell className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">Nessuna priorità operativa</p>
            <p className="mt-1 text-xs text-muted-foreground">Quando una situazione richiede attenzione, Martina la mostrerà qui.</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[320px]">
            <div className="px-2 py-1">
              {rooms.map((room) => {
                const title = room.identityTitle ?? room.title;
                const detail = room.presentation?.minimumMove ?? firstLine(room.brief);
                return (
                  <DropdownMenuItem
                    key={room.id}
                    className="my-1 h-auto cursor-pointer items-start rounded-none p-3 focus:bg-muted"
                    onSelect={() => {
                      setOpen(false);
                      navigate(martinaRoomPath(room.id));
                    }}
                  >
                    <span className="flex min-w-0 flex-1 items-start">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">{title}</span>
                        {detail && <span className="mt-1 block line-clamp-2 text-xs text-muted-foreground">{detail}</span>}
                      </span>
                      <span className="ml-3 shrink-0 text-xs font-medium text-primary">{room.attentionLane ?? 'Apri'}</span>
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </div>
          </ScrollArea>
        )}
        
        <div className="h-px bg-border"></div>
        <div className="p-2">
          <Link href="/">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-full justify-center rounded-none text-xs text-muted-foreground hover:bg-muted"
            >
              Apri la dashboard Martina
            </Button>
          </Link>
          <Link href="/notifications" className="mt-1 block">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-center rounded-none text-xs text-muted-foreground hover:bg-muted"
            >
              Apri archivio notifiche
            </Button>
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationIndicator;
